import React from 'react';
import { FaBookmark } from 'react-icons/fa';
import { SiPocket, SiX, SiPinboard } from 'react-icons/si';

import useClipboard from '../../hooks/useClipboard';

interface BookmarkButtonsProps {
  url: string;
  title: string;
  description?: string;
}

interface BookmarkService {
  name: string;
  icon: React.ReactNode;
  color: string;
  hoverColor: string;
  url: (params: { url: string; title: string; description?: string }) => string;
}

const BookmarkButtons: React.FC<BookmarkButtonsProps> = ({ url, title, description = '' }) => {
  const { copyMessage, copyToClipboard } = useClipboard();
  const services: BookmarkService[] = [
    {
      name: 'Raindrop.io',
      icon: (
        <img
          src="https://cdn.brandfetch.io/idPN0Xpy11/theme/dark/logo.svg?c=1bxid64Mup7aczewSAYMX&t=1721186721637"
          alt="Raindrop.io"
          width={16}
          height={16}
          className="filter brightness-0 invert"
          onError={e => {
            // フォールバック: 絵文字
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const fallback = document.createElement('span');
            fallback.textContent = '💧';
            fallback.className = 'text-blue-400';
            target.parentElement?.appendChild(fallback);
          }}
        />
      ),
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600',
      url: ({ url, title }) =>
        `https://app.raindrop.io/add?link=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
    },
    {
      name: 'Pocket',
      icon: <SiPocket size={16} />,
      color: 'bg-red-500',
      hoverColor: 'hover:bg-red-600',
      url: ({ url }) => `https://getpocket.com/edit?url=${encodeURIComponent(url)}`,
    },
    {
      name: 'X (Twitter)',
      icon: <SiX size={16} />,
      color: 'bg-black',
      hoverColor: 'hover:bg-gray-800',
      url: ({ url, title }) =>
        `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
    },
    {
      name: 'はてブ',
      icon: (
        <svg width="30" height="30" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
          <path
            fill="#0084d7"
            d="M40,6H24H8C6.895,6,6,6.895,6,8v16v16c0,1.105,0.895,2,2,2h16h16c1.105,0,2-0.895,2-2V24V8	C42,6.895,41.105,6,40,6z"
          ></path>
          <path
            fill="#027ad4"
            d="M6,24v12.706C6,39.63,8.37,42,11.294,42H24h16c1.105,0,2-0.895,2-2V24H6z"
          ></path>
          <path
            fill="#fff"
            d="M29.295,16.059h3.706v10.589h-3.706V16.059z M25.863,24.561c-0.627-0.701-1.499-1.095-2.618-1.18	c0.995-0.271,1.718-0.668,2.175-1.198c0.455-0.523,0.681-1.233,0.681-2.127c0-0.707-0.155-1.333-0.454-1.874	c-0.307-0.537-0.749-0.967-1.329-1.29c-0.508-0.279-1.111-0.476-1.814-0.592c-0.707-0.113-1.943-0.169-3.718-0.169h-4.315v15.737	h4.446c1.786,0,3.074-0.063,3.862-0.182c0.787-0.124,1.447-0.332,1.981-0.617c0.661-0.349,1.165-0.845,1.517-1.483	c0.354-0.64,0.53-1.378,0.53-2.221C26.807,26.198,26.492,25.26,25.863,24.561z M18.455,19.62h0.921c1.065,0,1.78,0.12,2.149,0.359	c0.364,0.24,0.55,0.655,0.55,1.247c0,0.569-0.198,0.969-0.589,1.204c-0.396,0.231-1.118,0.347-2.175,0.347h-0.856L18.455,19.62	L18.455,19.62z M22.109,28.645c-0.419,0.257-1.141,0.384-2.154,0.384h-1.499V25.59h1.564c1.041,0,1.76,0.131,2.143,0.392	c0.389,0.262,0.58,0.724,0.58,1.387C22.741,27.963,22.532,28.389,22.109,28.645z"
          ></path>
          <path
            fill="#e8e9e9"
            d="M25.863,24.562C25.665,24.34,25.434,24.16,25.187,24H14.471v7.869h4.446	c1.786,0,3.074-0.063,3.862-0.182c0.787-0.124,1.447-0.332,1.981-0.617c0.661-0.349,1.165-0.845,1.517-1.483	c0.354-0.64,0.53-1.378,0.53-2.221C26.807,26.199,26.493,25.261,25.863,24.562z M22.109,28.645	c-0.419,0.257-1.141,0.384-2.154,0.384h-1.499V25.59h1.564c1.041,0,1.76,0.131,2.143,0.392c0.389,0.262,0.58,0.724,0.58,1.387	C22.741,27.963,22.532,28.389,22.109,28.645z"
          ></path>
          <rect width="3.706" height="2.647" x="29.295" y="24" fill="#e8e9e9"></rect>
          <path
            fill="#e8e9e9"
            d="M31.148,27.706c-1.17,0-2.118,0.947-2.118,2.117s0.948,2.118,2.118,2.118	c1.169,0,2.117-0.948,2.117-2.118S32.317,27.706,31.148,27.706z"
          ></path>
        </svg>
      ),
      color: 'bg-green-600',
      hoverColor: 'hover:bg-green-700',
      url: ({ url, title }) =>
        `https://b.hatena.ne.jp/entry/panel/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
    },
    {
      name: 'Pinboard',
      icon: <SiPinboard size={16} />,
      color: 'bg-indigo-600',
      hoverColor: 'hover:bg-indigo-700',
      url: ({ url, title, description }) =>
        `https://pinboard.in/add?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}&description=${encodeURIComponent(description || '')}`,
    },
  ];

  const handleBookmark = (service: BookmarkService) => {
    const bookmarkUrl = service.url({ url, title, description });
    window.open(bookmarkUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="mb-6">
      <div className="flex items-center mb-3">
        <FaBookmark className="mr-2 text-gray-600" />
        <span className="font-medium text-gray-700">ブックマーク・シェア</span>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        {/* 全サービスを1行で表示 */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {services.map((service, index) => (
            <button
              key={index}
              onClick={() => handleBookmark(service)}
              className={`flex items-center justify-center px-3 py-2 ${service.color} ${service.hoverColor} text-white rounded-md text-sm font-medium transition-colors`}
              title={`${service.name}に保存`}
            >
              <span className="mr-2 flex items-center">{service.icon}</span>
              <span className="hidden sm:inline truncate">{service.name}</span>
            </button>
          ))}
        </div>

        {/* コピー機能 */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <button
            onClick={() => copyToClipboard(url)}
            className="flex items-center px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md text-sm font-medium transition-colors"
          >
            <span className="mr-2">📋</span>
            URLをコピー
          </button>
          {copyMessage && (
            <div className="mt-2 p-2 bg-green-100 text-green-700 text-sm rounded-md">
              {copyMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookmarkButtons;
